import { RestrictionName } from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";

// The only restriction names confirmed to have a real, persisting
// UpdateOrgRestrictionAsAdmin implementation. Every other restrictor either
// silently no-ops on that RPC (returns the current value, ignoring the write)
// or returns an empty response — see the Periscope write-UI design doc
// (Confluence 5905383609, Finding 2) before adding to this list. Confirm the
// specific restrictor's admin-write path is real, not shape alone, before
// adding a new entry (restrictors have been migrated onto
// TierBasedFeatureRestrictor after that doc was written, e.g.
// GROUPS_CREATE_DYNAMIC_GROUP/SITES_MANAGE_RULES in commit 75de927 — check
// current dispatch in api-restrictions' restrictorMap, not just the doc).
//
// Literal strings, matching the map keys used by GetOrgRestrictions'
// `restrictions` map (keyed by the full proto enum name, not the numeric
// value) — not derived from the RestrictionName enum's reverse mapping, which
// resolves to the short (unprefixed) member name instead.
export const EDITABLE_ORG_RESTRICTION_NAMES: ReadonlySet<string> = new Set([
  // Enterprise-plan gates backed by TierBasedFeatureRestrictor (FG-7595).
  "RESTRICTION_NAME_SSO_ENFORCE_SSO_ONLY",
  "RESTRICTION_NAME_SCIM_FEATURE_GATE",
  "RESTRICTION_NAME_DELEGATED_MANAGEMENT_FEATURE_GATE",
  "RESTRICTION_NAME_NOTIFICATIONS_LOCK_SETTINGS",
  "RESTRICTION_NAME_NAVIGATION_ORG_MANAGED",
  "RESTRICTION_NAME_AUDIT_HISTORY_ACCESS",
  "RESTRICTION_NAME_AUDIT_HISTORY_RETENTION_LIMIT",
  "RESTRICTION_NAME_GROUPS_CREATE_DYNAMIC_GROUP",
  "RESTRICTION_NAME_SITES_MANAGE_RULES",
  // Quota-shaped. SiteLimitRestrictor and RunLimitRestrictor each implement
  // Overridable with a genuine PutRestrictionByID, and both are registered in
  // all three of api-restrictions' DynamoDB switches — so the admin write
  // actually persists.
  "RESTRICTION_NAME_MANUFACTURING_SITE_LIMIT",
  "RESTRICTION_NAME_MANUFACTURING_RUN_LIMIT",
]);

// Names whose restrictor implements DeleteOrgOverrideAsAdmin with a real
// delete. That is a different method from the one the set above vets, and it
// is opt-in — a restrictor with a working admin write may still answer
// Unimplemented here — so confirm it per name rather than reusing that list.
export const REMOVABLE_ORG_RESTRICTION_NAMES: ReadonlySet<string> = new Set([
  // Both go through api-restrictions' shared deleteOrgOverride helper, which
  // calls DeleteRestrictionByID.
  "RESTRICTION_NAME_MANUFACTURING_SITE_LIMIT",
  "RESTRICTION_NAME_MANUFACTURING_RUN_LIMIT",
]);

// The map keys above are the full proto enum names; the generated enum's
// members are the unprefixed ones. Returns undefined for a name this client's
// proto version does not know.
export const toRestrictionName = (name: string): RestrictionName | undefined =>
  RestrictionName[
    name.replace("RESTRICTION_NAME_", "") as keyof typeof RestrictionName
  ];
